import React from 'react'
import CircularProgress from '@material-ui/core/CircularProgress'
import { AdCreative } from '@marketing-milk/interfaces'
import { CreativeStatus } from 'components/creative-status/CreativeStatus'
import { CreativeMedia } from '../../../creatives/CreativeCard/CreativeMedia'
import {
  isAdImage,
  isAdVideo,
  isDynamicCreative,
} from '../../../campaigns/helpers/campaign.helpers'
import { accent, accentSoft, radius, SurfacePalette, type } from '../../tokens'
import { CommentIndicator } from '../../components/CommentIndicator'
import { useUnresolvedCountLookup } from '../UnresolvedCommentsContext'

/**
 * Packet-local thumbnail. For dynamic creatives whose lead asset is a video we
 * render the `thumbnail` poster (never-blank, consistent with the dynamic asset
 * pool) instead of the shared `CreativeMedia`'s autoplaying `<video src=source>` —
 * patching `CreativeMedia` itself would change every other creative screen. All
 * non-dynamic-video cases delegate to the shared `CreativeMedia`.
 */
const CardThumbnail = ({ creative }: { creative: AdCreative }) => {
  if (isDynamicCreative(creative)) {
    const lead = [...creative.dynamicImages, ...creative.dynamicVideos][0]
    if (isAdVideo(lead) && lead.thumbnail) {
      return (
        <img
          src={lead.thumbnail}
          alt="creative video thumbnail"
          className="h-full w-full object-cover rounded-t-lg"
        />
      )
    }
    if (isAdImage(lead)) {
      return (
        <img src={lead.url} alt={lead.name} className="h-full w-full object-cover rounded-t-lg" />
      )
    }
  }
  return <CreativeMedia creative={creative} className="h-full w-full" />
}

interface Props {
  palette: SurfacePalette
  creatives?: AdCreative[]
  loading?: boolean
  /** True when the packet has no bound sprint to load creatives from. */
  noSprint?: boolean
  /**
   * When provided, clicking a card's media opens the live Facebook mobile-feed
   * preview — the surface that shows the full ad and hosts the comment / approve
   * / open-source actions. Enabled in both Edit and Present.
   */
  onPreview?: (creative: AdCreative) => void
  /**
   * When provided, hovering a card warms that creative's live preview into the
   * shared cache so the preview modal opens instantly. One creative at a time;
   * no-op without `onPreview`.
   */
  onPrefetch?: (creative: AdCreative) => void
}

/** Shared shape of the media-tile badges; each usage sets its own background. */
const badgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '3px 8px',
  borderRadius: radius.sm,
  color: '#fff',
  fontFamily: type.family.sans,
  fontSize: type.size.eyebrow,
  fontWeight: type.weight.semibold,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
}

const Muted = ({ palette, children }: { palette: SurfacePalette; children: React.ReactNode }) => (
  <p style={{ margin: 0, fontSize: type.size.body, color: palette.textMuted }}>{children}</p>
)

/**
 * Shared grid of sprint creatives used by both the edit (`CreativeSection`) and
 * Present (`PresentCreativeSection`) hosts. Each card is its own `creative`
 * comment anchor keyed by the bare creative id (matching CreativeWizard so the
 * thread is shared) and reveals a per-card comment trigger on hover. The shared
 * click delegation in `useCommentAnchors` resolves a card trigger to the
 * nearest anchor ancestor — the card — leaving the section-chrome `section`
 * anchor (further up) untouched.
 */
export const CreativeCardList = ({
  palette,
  creatives,
  loading,
  noSprint,
  onPreview,
  onPrefetch,
}: Props) => {
  const countFor = useUnresolvedCountLookup()
  if (noSprint) return <Muted palette={palette}>This packet isn’t linked to a sprint.</Muted>
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: palette.textMuted }}>
        <CircularProgress size={18} style={{ color: palette.accent }} />
        <span style={{ fontSize: type.size.body }}>Loading creatives…</span>
      </div>
    )
  }
  if (!creatives || creatives.length === 0) {
    return <Muted palette={palette}>No creatives in this sprint yet.</Muted>
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: 20,
      }}
    >
      {creatives.map(creative => {
        // Dynamic creatives open the grouped asset pool (not the FB feed preview),
        // so flag them up-front: accent border + a "Dynamic" badge, and a
        // "View assets" hover label instead of "Preview ad".
        const dynamic = isDynamicCreative(creative)
        return (
          <div
            key={creative.id}
            className="group"
            data-comment-anchor-type="creative"
            data-comment-anchor-id={String(creative.id)}
            data-comment-label={creative.name}
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: radius.md,
              border: `1px solid ${dynamic ? accent : palette.hairline}`,
              boxShadow: dynamic ? `0 0 0 1px ${accentSoft}` : undefined,
              background: palette.surface,
              overflow: 'hidden',
            }}
          >
            {/*
             * The card is a gallery tile: media is the click target that opens the
             * preview (where the full ad + comment / approve / open-source actions
             * live). The status chip and comment overlay below are SIBLINGS of this
             * element, not children, so clicking the comment trigger never fires
             * the preview handler and still bubbles to the `useCommentAnchors` root.
             */}
            <div
              role={onPreview ? 'button' : undefined}
              tabIndex={onPreview ? 0 : undefined}
              aria-label={onPreview ? `Preview ${creative.name}` : undefined}
              onClick={onPreview ? () => onPreview(creative) : undefined}
              onMouseEnter={onPrefetch && !dynamic ? () => onPrefetch(creative) : undefined}
              onKeyDown={
                onPreview
                  ? e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onPreview(creative)
                      }
                    }
                  : undefined
              }
              style={{
                position: 'relative',
                aspectRatio: '1 / 1',
                // A flex item's automatic minimum size lets a portrait <video>'s
                // intrinsic height override the aspect-ratio square; zero it so
                // videos crop to the tile like images do.
                minHeight: 0,
                background: palette.bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: onPreview ? 'pointer' : 'default',
              }}
            >
              {/*
               * Reuse the app-wide CreativeMedia thumbnail (image/video/carousel/
               * dynamic with broken-link fallbacks) rather than the single
               * denormalized s3Preview field, which is empty for most creatives.
               */}
              <CardThumbnail creative={creative} />
              {onPreview && (
                // Faint hover overlay so the media reads as an interactive preview
                // target. group-hover is driven by the card wrapper's `group` class.
                <div
                  className="opacity-0 group-hover:opacity-100"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0, 0, 0, 0.4)',
                    color: '#fff',
                    fontFamily: type.family.sans,
                    fontSize: type.size.eyebrow,
                    fontWeight: type.weight.medium,
                    transition: 'opacity 120ms ease',
                    pointerEvents: 'none',
                  }}
                >
                  {dynamic ? 'View assets' : 'Preview ad'}
                </div>
              )}

              {/* Badges — anchored to the bottom-left of the MEDIA tile (this
                relatively-positioned wrapper), not the card, so they sit on the
                thumbnail rather than over the caption below. */}
              {(dynamic || creative.leadCardUsed) && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 10,
                    left: 10,
                    pointerEvents: 'none',
                    display: 'flex',
                    gap: 6,
                  }}
                >
                  {dynamic && (
                    <div style={{ ...badgeStyle, background: accent }}>
                      <i className="fa fa-random" style={{ fontSize: 10 }} />
                      Dynamic
                    </div>
                  )}
                  {/* Lead-form chip: this creative's preview embeds the lead card
                    journey, so the AE can spot lead ads before opening anything. */}
                  {creative.leadCardUsed && (
                    <div style={{ ...badgeStyle, background: 'rgba(0, 0, 0, 0.55)' }}>
                      <i className="fa fa-address-card" style={{ fontSize: 10 }} />
                      Lead form
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Status chip overlay (top-left), keeping its own CreativeStatus badge. */}
            <div style={{ position: 'absolute', top: 10, left: 10, pointerEvents: 'none' }}>
              <CreativeStatus creative={creative} className="shadow-sm" />
            </div>

            {/* Comment overlay (top-right), sibling of the media region. */}
            <div style={{ position: 'absolute', top: 10, right: 10 }}>
              <CommentIndicator
                palette={palette}
                label={creative.name}
                iconOnly
                unresolvedCount={countFor('creative', String(creative.id))}
                style={{
                  background: 'rgba(0, 0, 0, 0.55)',
                  color: '#fff',
                  borderRadius: radius.sm,
                }}
              />
            </div>

            <div style={{ padding: 14 }}>
              <span
                style={{
                  fontFamily: type.family.sans,
                  fontSize: type.size.body,
                  fontWeight: type.weight.medium,
                  color: palette.text,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.35,
                }}
                title={creative.name}
              >
                {creative.name}
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}
